import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { SearchService } from '../search.service';
import { StructureList } from '../../shared/models/structure-list';
import { LdwRouterService } from '../../route/ldw-router.service';
import { ActivatedRoute, Router } from '@angular/router';
import { SearchOptions } from '../search-options';
import { InfoService } from '../../shared/services/info.service';
import { SearchDefinition } from '../search-definition';
import { catchError, distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';
import { combineLatest, EMPTY, Observable, of, Subscription } from 'rxjs';
import { WebdokuService } from '../../web-doku/webdoku.service';
import { RBCheck, ViewModelProperty } from '@ldw/roesberg';
import { SettingsService } from '../../shared/settings/settings.service';
import { MatDialog } from '@angular/material/dialog';
import { QRCodeComponent } from '../../scan/qr-code/qr-code.component';
import { SelectionArguments } from '../../shared/models/selection-arguments';
import { StructureType } from '../../shared/enums/structure-type';
import { SelectionType } from '../../shared/enums/selection-type';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'ldw-search-page',
    templateUrl: './search-page.component.html',
    styleUrls: ['./search-page.component.css'],
    standalone: false
})
export class SearchPageComponent implements OnInit, OnDestroy {
  private webDokuService = inject(WebdokuService);
  private routerService = inject(LdwRouterService);
  private searchService = inject(SearchService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private infoService = inject(InfoService);
  private settingsService = inject(SettingsService);
  private translate = inject(TranslateService);
  dialog = inject(MatDialog);

  public searchResult: StructureList = new StructureList();
  public searchHeaderText = '';
  public searchText = '';
  public detailDocumentId = '';

  searchDefinition: SearchDefinition | undefined = undefined;
  // some state variables
  isSearching = false;
  isNotFound = false;
  hasData = false;
  public hasDetail = false;
  public allowSplitMode = true;
  searchEnabled = true;
  searchMetadata = false;
  error = '';
  wdId = '';

  wdSubscription: Subscription = new Subscription();
  query = '';

  ngOnInit() {
    this.infoService.clearStatus();

    this.wdSubscription.add(
      combineLatest([this.route.paramMap, this.route.queryParamMap])
        .pipe(
          map(([paramMap, queryParamMap]) => ({
            wd: paramMap.get('wd') ?? '',
            query: queryParamMap.get('query') ?? '',
          })),
          distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
          switchMap(({ wd, query }: { wd: string; query: string }) => {
            this.isSearching = true;
            this.wdId = wd;
            this.query = query;

            return this.webDokuService.selectWebDoku(this.wdId);
          }),
          switchMap((selectWdOk) => {
            return this.searchFields(selectWdOk, this.wdId, this.query);
          }),
          tap(([searchFields, searchRes]) => {
            // searchFields.clone();
            this.SetSearchFieldsAndResult(searchFields, searchRes);
            this.isSearching = false;
          })
        )
        .subscribe()
    );
  }

  searchFields(selectWdOk: boolean, wdId: string, query: string) {
    if (!selectWdOk) {
      this.isSearching = false;
      this.routerService.errorStop('WEBDOKUSELECTERROR');
      return EMPTY;
    }
    if (query !== '') {
      return this.GetSearchFieldsAndResults(this.wdId, query);
    } else {
      return this.GetSearchFieldsWithoutResult(this.wdId);
    }
  }

  private SetSearchFieldsAndResult(searchDefinition: SearchDefinition, searchResult: StructureList | undefined): void {
    console.log('set search result', searchDefinition);

    if (
      searchDefinition?.luceneSearchField != undefined &&
      searchResult != undefined &&
      !RBCheck.isNullOrEmpty(searchResult.searchText)
    ) {
      searchDefinition.luceneSearchField.value = searchResult.searchText;
    }

    if (searchResult == undefined && searchDefinition != null) {
      searchDefinition.searchMetaData = this.settingsService.settings.searchSettings.dbSearchAsDefaultEnabled;
      searchDefinition.isWildCard = this.settingsService.settings.searchSettings.isWildCard;
    }

    this.searchDefinition = searchDefinition;
    if (searchResult) {
      this.searchFound(searchResult);
    }
  }

  private GetSearchFieldsWithoutResult(webDokuId: string): Observable<[SearchDefinition, StructureList | undefined]> {
    return combineLatest([this.searchService.getSearchFields(webDokuId), of(new StructureList())]);
  }

  private setSearchFieldValues(searchDefinition: SearchDefinition, options: SearchOptions): SearchDefinition {
    // search values
    this.setVMPfromOptions(searchDefinition.luceneSearchField, options);
    this.setVMPfromOptions(searchDefinition.docStatusSearchField, options);
    this.setVMPfromOptions(searchDefinition.databaseSearchField, options);
    this.setVMPGroupfromOptions(searchDefinition.docFields, options);
    this.setVMPGroupfromOptions(searchDefinition.lncFields, options);
    this.setVMPGroupfromOptions(searchDefinition.loopFields, options);
    this.setVMPGroupfromOptions(searchDefinition.lsnFields, options);
    this.setVMPGroupfromOptions(searchDefinition.nodeFields, options);
    this.setVMPGroupfromOptions(searchDefinition.customFields, options);
    // search settings
    searchDefinition.searchMetaData = options.searchMetaData;
    searchDefinition.navigationMode = options.navigationMode;
    searchDefinition.isWildCard = options.isWildCard;

    console.log('setSearchFieldValues', searchDefinition);
    return searchDefinition;
  }

  setVMPfromOptions(vmp: ViewModelProperty | undefined, options: SearchOptions) {
    if (vmp != null) {
      vmp.value = options.searchValues.find((s) => s.key === vmp?.fieldName)?.value;
    }
  }

  private setVMPGroupfromOptions(nodeFields: ViewModelProperty[], options: SearchOptions) {
    if (nodeFields == null) return;
    nodeFields.forEach((vmp) => {
      this.setVMPfromOptions(vmp, options);
    });
  }

  private GetSearchFieldsAndResults(
    webDokuId: string,
    query: string
  ): Observable<[SearchDefinition, StructureList | undefined]> {
    const options = SearchOptions.fromJson(query);
    options.webDokuId = webDokuId;
    this.setSearchHeader(options.getSearchHeader());
    this.searchText = options.getSearchText();
    this.searchMetadata = options.searchMetaData;
    return combineLatest([
      this.searchService
        .getSearchFields(webDokuId)
        .pipe(map((searchDefs) => this.setSearchFieldValues(searchDefs, options))),
      this.searchService.search(options).pipe(
        catchError((error) => {
          this.searchError(error);
          return of(undefined);
        })
      ),
    ]);
  }

  ngOnDestroy(): void {
    this.wdSubscription.unsubscribe();
  }

  /* init must be done, after search definition is retrieved
  init(def: SearchDefinition) {


    const tokenSearch = this.route.snapshot.paramMap.get('tokenSearch');
    if (tokenSearch != null && tokenSearch.toLocaleUpperCase() == 'TRUE') {
      this.startSearch(searchText, true);
    } else {
      this.startSearch(searchText);
    }
    }
       */

  hasError(): boolean {
    return this.error != '';
  }

  clearErrorStates() {
    this.isNotFound = false;
    this.error = '';
  }

  reopenSearch() {
    this.searchEnabled = true;
  }

  scan(searchoptions: SearchOptions) {
    this.infoService.clearStatus();
    if (this.isSearching) {
      return;
    }

    this.clearErrorStates();
    this.clearSearchHeader();
    this.hasData = false;

    const dialogRef = this.dialog.open(QRCodeComponent, {
      maxWidth: '100vw',
      maxHeight: '100vh',
      height: '100%',
      width: '100%',
      // panelClass: 'full-screen-modal',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (RBCheck.isNullOrEmpty(result)) {
        return;
      }
      console.log(`Dialog result: ${result}`);
      const options = new SearchOptions();
      options.webDokuId = this.wdId;
      options.searchMetaData = searchoptions.searchMetaData;
      options.addSearchText(result);
      options.isTokenSearch = true;

      // serialze options
      // replace route params
      this.router.navigate(['./'], {
        queryParams: { query: this.routerService.serializeSearchOptions(options) },
        relativeTo: this.route,
      });
    });
  }

  clearForm(options: SearchOptions) {
    this.clearErrorStates();
    this.clearSearchHeader();
    this.hasData = false;
  }

  submitForm(options: SearchOptions) {
    this.infoService.clearStatus();
    if (this.isSearching) {
      return;
    }

    this.clearErrorStates();
    this.clearSearchHeader();
    this.hasData = false;

    options.webDokuId = this.wdId;
    // force change of routeparam, also search values may not have changed
    options.rValue = Math.floor(Math.random() * 10000);
    // serialize options
    // replace route params
    this.router.navigate(['./'], {
      queryParams: { query: this.routerService.serializeSearchOptions(options) },
      relativeTo: this.route,
    });
  }

  private searchFound(res: StructureList) {
    this.isSearching = false;
    if (res.data == undefined || (res.data.length < 1 && this.query)) {
      this.clearSearchHeader();
      this.reopenSearch();
      this.hasData = false;
      this.isNotFound = true;
      return;
    }
    this.searchResult = res;
    if (!this.query) {
      this.reopenSearch();
      this.infoService.setLoadedItems(this.searchResult.data.length);
      this.hasData = false;
      this.searchEnabled = true;
    } else {
      this.infoService.setLoadedItems(this.searchResult.data.length);
      this.hasData = true;
      this.searchEnabled = false;
    }
  }

  private searchError(error: any) {
    this.clearSearchHeader();
    this.reopenSearch();
    this.isSearching = false;

    if (!RBCheck.isNullOrEmpty(error)) {
      if (error.status == 400 && error.error != null && !RBCheck.isNullOrEmpty(error.error.message)) {
        if (error.error.message.includes(':')){
          this.error = this.translate.instant('ERROR.' + error.error.message.split(':')[0]);
          return;
        }
      }
    }

    this.error = error?.message ?? 'ERROR.UNKNOWN';
  }

  private clearSearchHeader() {
    this.searchHeaderText = '';
    this.searchMetadata = false;
  }

  private setSearchHeader(text: string) {
    this.searchHeaderText = text;
  }

  showToolTip(): boolean {
    return this.settingsService.settings.viewMode.showTooltips;
  }
  public get useSplitMode(): boolean {
    return this.allowSplitMode && this.settingsService.settings.documentMode.useListPreview;
  }

  detailVisibleChanged(value: boolean) {
    this.hasDetail = value;
  }
  public closeDetail() {
    this.hasDetail = false;
    this.detailDocumentId = '';
  }

  activateInternalEditorView(docId: string) {
    this.hasDetail = true;
    this.detailDocumentId = docId;
  }

  itemSelected(args: SelectionArguments) {
    if (args == undefined || args.id == undefined) {
      return;
    }
    this.selectItem(args);
  }
  selectItem(args: SelectionArguments) {
    if (args == undefined || args.id == undefined) {
      return;
    }
    if (args.structureType != StructureType.Document) {
      return;
    }

    if (args.action == SelectionType.Preview) {
      this.activateInternalEditorView(args.id);
      return;
    }

    let selType = args.action;
    if (args.action == SelectionType.Selected) {
      selType = SelectionType.View;
    }

    /*
    if (this.updateRouteOnSelection) {
      console.log('update from selection');
      console.log('last node: ' + this.lastNodeId);
      console.log('selection: ' + args.id);
      this.updateRoute(this.lastNodeId, args.id);
    }
     */
    this.routerService.navigateToDocumentBySelectionType(selType, args.id);
  }
}
